import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Key, 
  Copy, 
  Download, 
  Upload, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  FileText,
  AlertTriangle,
  Info,
  Eye,
  EyeOff,
  Trash2,
  Shield,
  Clock,
  Database,
  Code,
  ArrowLeftRight,
  Search,
  Lock,
  CheckCircle,
  Building,
  Award,
  Zap,
  Star,
  Timer,
  Layers
} from 'lucide-react';

interface PBKDF2Settings {
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  outputLength: number;
  iterations: number;
  hashAlgorithm: HashAlgorithm;
  saltLength: number;
  autoGenerateSalt: boolean;
  customSalt: string;
  showProgress: boolean;
  enableComparison: boolean;
  showAnalysis: boolean;
  autoGenerate: boolean;
  caseSensitive: boolean;
  includeTimestamp: boolean;
  showSecurityInfo: boolean;
  showEnterpriseMode: boolean;
  minIterations: number;
  useCustomIterations: boolean;
}

interface PBKDF2Result {
  id: string;
  input: string;
  derivedKey: string;
  salt: string;
  iterations: number;
  outputLength: number;
  hashAlgorithm: HashAlgorithm;
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  timestamp: Date;
  inputLength: number;
  processingTime: number;
  isVerified: boolean;
  securityLevel: SecurityLevel;
  complianceScore: number;
  strengthScore: number;
}

interface PBKDF2Analytics {
  totalHashes: number;
  totalProcessingTime: number;
  averageInputLength: number;
  averageIterations: number;
  formatDistribution: Record<InputFormat, number>;
  algorithmDistribution: Record<HashAlgorithm, number>;
  hashHistory: Array<{
    timestamp: Date;
    inputLength: number;
    processingTime: number;
    iterations: number;
    securityLevel: SecurityLevel;
    strengthScore: number;
  }>;
  securityScore: number;
  strongPasswords: number;
  enterpriseModeUsage: number;
  complianceScore: number;
  averageStrengthScore: number;
}

type InputFormat = 'text' | 'file' | 'hex' | 'base64' | 'binary' | 'utf8';
type OutputFormat = 'hex' | 'base64' | 'binary' | 'decimal';
type HashAlgorithm = 'SHA1' | 'SHA256' | 'SHA512';
type SecurityLevel = 'weak' | 'moderate' | 'strong' | 'enterprise' | 'government';

const PBKDF2HashGenerator: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [pbkdf2Result, setPbkdf2Result] = useState<PBKDF2Result | null>(null);
  const [compareHash, setCompareHash] = useState<string>('');
  const [compareSalt, setCompareSalt] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copiedItem, setCopiedItem] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [settings, setSettings] = useState<PBKDF2Settings>({
    inputFormat: 'text',
    outputFormat: 'hex',
    outputLength: 32,
    iterations: 100000,
    hashAlgorithm: 'SHA256',
    saltLength: 16,
    autoGenerateSalt: true,
    customSalt: '',
    showProgress: true,
    enableComparison: true,
    showAnalysis: true,
    autoGenerate: false,
    caseSensitive: true,
    includeTimestamp: false,
    showSecurityInfo: true,
    showEnterpriseMode: false,
    minIterations: 10000,
    useCustomIterations: false
  });

  const [analytics, setAnalytics] = useState<PBKDF2Analytics>({
    totalHashes: 0,
    totalProcessingTime: 0,
    averageInputLength: 0,
    averageIterations: 0,
    formatDistribution: { text: 0, file: 0, hex: 0, base64: 0, binary: 0, utf8: 0 },
    algorithmDistribution: { SHA1: 0, SHA256: 0, SHA512: 0 },
    hashHistory: [],
    securityScore: 0,
    strongPasswords: 0,
    enterpriseModeUsage: 0,
    complianceScore: 0,
    averageStrengthScore: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "PBKDF2 Hash Generator";
    const dynamicTitle = analytics.totalHashes > 0 
      ? `${baseTitle} - ${analytics.totalHashes} Enterprise PBKDF2 Keys Generated`
      : `${baseTitle} - Enterprise Password-Based Key Derivation Function`;
    
    const description = analytics.totalHashes > 0
      ? `Enterprise PBKDF2 password hash generator with ${analytics.totalHashes} keys derived. Generate secure password hashes, derive cryptographic keys, and implement enterprise authentication. ${analytics.complianceScore}% security compliance.`
      : "Generate enterprise-grade PBKDF2 password hashes and cryptographic keys online. Advanced password-based key derivation with configurable iterations, salt, and multiple hash algorithms for secure authentication systems.";

    const keywords = [
      "pbkdf2 hash generator",
      "password based key derivation function",
      "pbkdf2 calculator", 
      "enterprise password hashing",
      "secure password storage",
      "pbkdf2 encoder",
      "cryptographic key derivation",
      "password hash generator",
      "enterprise authentication",
      "pbkdf2 hash comparison",
      "secure password hashing",
      "pbkdf2 password encoder",
      "enterprise security hash",
      "pbkdf2 hash tool",
      "password based encryption",
      "pbkdf2 key generator"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalHashes, analytics.complianceScore]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "PBKDF2 Hash Generator",
    "description": seoData.description,
    "url": "https://example.com/pbkdf2-hash-generator",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "PBKDF2 key derivation",
      "Enterprise password hashing",
      "Configurable iterations and salt",
      "Multiple hash algorithms",
      "Secure authentication systems",
      "Enterprise compliance",
      "Password strength analysis",
      "Cryptographic key generation"
    ],
    "targetAudience": {
      "@type": "Audience",
      "audienceType": "Enterprise IT, Security Engineers, Backend Developers, Authentication Systems"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Enterprise Security Tools"
    },
    "softwareVersion": "1.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free for enterprise and development use"
  };

  // Simple crypto utilities for PBKDF2
  const generateSalt = useCallback((length: number): string => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }, []);

  const stringToUint8Array = useCallback((str: string): Uint8Array => {
    return new TextEncoder().encode(str);
  }, []);

  const hexToUint8Array = useCallback((hex: string): Uint8Array => {
    const bytes = hex.match(/.{2}/g);
    return new Uint8Array(bytes ? bytes.map(byte => parseInt(byte, 16)) : []);
  }, []);

  const uint8ArrayToHex = useCallback((array: Uint8Array): string => {
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }, []);

  const uint8ArrayToBase64 = useCallback((array: Uint8Array): string => {
    return btoa(String.fromCharCode.apply(null, Array.from(array)));
  }, []);

  const uint8ArrayToBinary = useCallback((array: Uint8Array): string => {
    return Array.from(array, byte => byte.toString(2).padStart(8, '0')).join('');
  }, []);

  const uint8ArrayToDecimal = useCallback((array: Uint8Array): string => {
    return Array.from(array, byte => byte.toString()).join('.');
  }, []);

  // PBKDF2 Implementation using Web Crypto API
  const pbkdf2 = useCallback(async (
    password: string, 
    salt: string, 
    iterations: number, 
    keyLength: number, 
    algorithm: HashAlgorithm
  ): Promise<string> => {
    const passwordBuffer = stringToUint8Array(password);
    const saltBuffer = typeof salt === 'string' ? hexToUint8Array(salt) : salt;
    
    const hashAlgorithmMap = {
      'SHA1': 'SHA-1',
      'SHA256': 'SHA-256',
      'SHA512': 'SHA-512'
    };

    try {
      // Import the password as a key
      const key = await crypto.subtle.importKey(
        'raw',
        passwordBuffer,
        'PBKDF2',
        false,
        ['deriveBits']
      );

      // Derive key using PBKDF2
      const derivedBits = await crypto.subtle.deriveBits(
        {
          name: 'PBKDF2',
          salt: saltBuffer,
          iterations: iterations,
          hash: hashAlgorithmMap[algorithm]
        },
        key,
        keyLength * 8 // Convert bytes to bits
      );

      return uint8ArrayToHex(new Uint8Array(derivedBits));
    } catch (error) {
      console.error('PBKDF2 derivation failed:', error);
      throw new Error('Failed to derive key using PBKDF2');
    }
  }, [stringToUint8Array, hexToUint8Array, uint8ArrayToHex]);

  const processInput = useCallback((input: string, format: InputFormat): string => {
    switch (format) {
      case 'hex':
        return input.replace(/[^0-9A-Fa-f]/g, '').match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('') || '';
      case 'base64':
        try {
          return atob(input);
        } catch {
          return input;
        }
      case 'binary':
        return input.replace(/[^01]/g, '').match(/.{8}/g)?.map(byte => String.fromCharCode(parseInt(byte, 2))).join('') || '';
      case 'utf8':
      case 'text':
      case 'file':
      default:
        return input;
    }
  }, []);

  const formatOutput = useCallback((derivedKey: string, format: OutputFormat): string => {
    const keyBytes = hexToUint8Array(derivedKey);
    
    switch (format) {
      case 'base64':
        return uint8ArrayToBase64(keyBytes);
      case 'binary':
        return uint8ArrayToBinary(keyBytes);
      case 'decimal':
        return uint8ArrayToDecimal(keyBytes);
      case 'hex':
      default:
        return derivedKey;
    }
  }, [hexToUint8Array, uint8ArrayToBase64, uint8ArrayToBinary, uint8ArrayToDecimal]);

  const calculatePasswordStrength = useCallback((password: string): number => {
    let score = 0;
    
    // Length scoring
    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 15;
    if (password.length >= 16) score += 15;
    
    // Character variety
    if (/[a-z]/.test(password)) score += 10;
    if (/[A-Z]/.test(password)) score += 10;
    if (/[0-9]/.test(password)) score += 10;
    if (/[^A-Za-z0-9]/.test(password)) score += 15;
    
    // Patterns and complexity
    if (!/(.)\1{2,}/.test(password)) score += 5; // No repeated characters
    if (!/123|abc|qwe/i.test(password)) score += 5; // No common sequences
    
    return Math.min(100, score);
  }, []);

  const calculateSecurityLevel = useCallback((
    iterations: number, 
    passwordStrength: number, 
    saltLength: number,
    algorithm: HashAlgorithm
  ): SecurityLevel => {
    let score = 0;
    
    // Iteration scoring
    if (iterations >= 10000) score += 2;
    if (iterations >= 50000) score += 2;
    if (iterations >= 100000) score += 2;
    if (iterations >= 300000) score += 2;
    
    // Password strength scoring
    if (passwordStrength >= 60) score += 2;
    if (passwordStrength >= 80) score += 2;
    
    // Salt length scoring
    if (saltLength >= 16) score += 1;
    if (saltLength >= 32) score += 1;
    
    // Algorithm scoring
    if (algorithm === 'SHA256') score += 1;
    if (algorithm === 'SHA512') score += 2;
    
    if (score <= 4) return 'weak';
    if (score <= 7) return 'moderate';
    if (score <= 10) return 'strong';
    if (score <= 12) return 'enterprise';
    return 'government';
  }, []);

  const calculateComplianceScore = useCallback((
    securityLevel: SecurityLevel, 
    iterations: number, 
    passwordStrength: number,
    algorithm: HashAlgorithm
  ): number => {
    let score = 0;
    
    // Base score for PBKDF2 usage
    score += 30;
    
    // Security level scoring
    switch (securityLevel) {
      case 'government': score += 40; break;
      case 'enterprise': score += 30; break;
      case 'strong': score += 20; break;
      case 'moderate': score += 10; break;
      case 'weak': score += 0; break;
    }
    
    // Iteration count compliance
    if (iterations >= 100000) score += 15;
    else if (iterations >= 50000) score += 10;
    else if (iterations >= 10000) score += 5;
    
    // Password strength compliance
    if (passwordStrength >= 80) score += 10;
    else if (passwordStrength >= 60) score += 5;
    
    // Algorithm compliance
    if (algorithm === 'SHA512') score += 5;
    else if (algorithm === 'SHA256') score += 3;
    
    return Math.min(100, score);
  }, []);

  const generatePBKDF2 = useCallback(async () => {
    if (!inputText.trim() && !uploadedFile) return;

    setIsGenerating(true);
    const startTime = performance.now();

    try {
      let processedInput = '';
      
      if (uploadedFile && settings.inputFormat === 'file') {
        const fileContent = await uploadedFile.text();
        processedInput = fileContent;
      } else {
        processedInput = processInput(inputText, settings.inputFormat);
      }

      if (!settings.caseSensitive) {
        processedInput = processedInput.toLowerCase();
      }

      if (settings.includeTimestamp) {
        processedInput += new Date().toISOString();
      }

      // Generate or use salt
      const salt = settings.autoGenerateSalt 
        ? generateSalt(settings.saltLength)
        : settings.customSalt || generateSalt(settings.saltLength);

      const inputLen = processedInput.length;
      const passwordStrength = calculatePasswordStrength(processedInput);
      
      // Derive key using PBKDF2
      const derivedKeyHex = await pbkdf2(
        processedInput,
        salt,
        settings.iterations,
        settings.outputLength,
        settings.hashAlgorithm
      );
      
      const formattedKey = formatOutput(derivedKeyHex, settings.outputFormat);
      const processingTime = performance.now() - startTime;
      
      const securityLevel = calculateSecurityLevel(
        settings.iterations,
        passwordStrength,
        settings.saltLength,
        settings.hashAlgorithm
      );
      
      const complianceScore = calculateComplianceScore(
        securityLevel,
        settings.iterations,
        passwordStrength,
        settings.hashAlgorithm
      );

      const result: PBKDF2Result = {
        id: Math.random().toString(36).substr(2, 9),
        input: uploadedFile ? `File: ${uploadedFile.name}` : inputText,
        derivedKey: formattedKey,
        salt: salt,
        iterations: settings.iterations,
        outputLength: settings.outputLength,
        hashAlgorithm: settings.hashAlgorithm,
        inputFormat: settings.inputFormat,
        outputFormat: settings.outputFormat,
        timestamp: new Date(),
        inputLength: inputLen,
        processingTime,
        isVerified: false,
        securityLevel,
        complianceScore,
        strengthScore: passwordStrength
      };

      setPbkdf2Result(result);

      // Update analytics
      setAnalytics(prev => {
        const total = prev.totalHashes + 1;
        const totalTime = prev.totalProcessingTime + processingTime;
        const avgLength = (prev.averageInputLength * prev.totalHashes + inputLen) / total;
        const avgIterations = (prev.averageIterations * prev.totalHashes + settings.iterations) / total;
        const avgStrength = (prev.averageStrengthScore * prev.totalHashes + passwordStrength) / total;
        
        const formatDist = { ...prev.formatDistribution };
        formatDist[settings.inputFormat]++;

        const algoDist = { ...prev.algorithmDistribution };
        algoDist[settings.hashAlgorithm]++;

        const historyEntry = {
          timestamp: new Date(),
          inputLength: inputLen,
          processingTime,
          iterations: settings.iterations,
          securityLevel,
          strengthScore: passwordStrength
        };

        const securityPoints = securityLevel === 'government' ? 100 : 
                              securityLevel === 'enterprise' ? 85 : 
                              securityLevel === 'strong' ? 70 : 
                              securityLevel === 'moderate' ? 50 : 25;
        const avgSecurity = (prev.securityScore * prev.totalHashes + securityPoints) / total;
        const avgCompliance = (prev.complianceScore * prev.totalHashes + complianceScore) / total;

        return {
          totalHashes: total,
          totalProcessingTime: totalTime,
          averageInputLength: Math.round(avgLength * 10) / 10,
          averageIterations: Math.round(avgIterations),
          formatDistribution: formatDist,
          algorithmDistribution: algoDist,
          hashHistory: [historyEntry, ...prev.hashHistory].slice(0, 100),
          securityScore: Math.round(avgSecurity * 10) / 10,
          strongPasswords: prev.strongPasswords + (passwordStrength >= 70 ? 1 : 0),
          enterpriseModeUsage: prev.enterpriseModeUsage + (settings.showEnterpriseMode ? 1 : 0),
          complianceScore: Math.round(avgCompliance * 10) / 10,
          averageStrengthScore: Math.round(avgStrength * 10) / 10
        };
      });
    } catch (error) {
      console.error('PBKDF2 generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [
    inputText, 
    uploadedFile, 
    settings, 
    processInput, 
    formatOutput, 
    generateSalt, 
    pbkdf2, 
    calculatePasswordStrength, 
    calculateSecurityLevel, 
    calculateComplianceScore
  ]);

  const compareResults = useCallback((): boolean => {
    if (!pbkdf2Result || !compareHash.trim()) return false;
    
    if (compareSalt.trim()) {
      // Compare with custom salt - would need to regenerate
      return false; // Complex comparison requiring regeneration
    }
    
    return pbkdf2Result.derivedKey.toLowerCase() === compareHash.toLowerCase();
  }, [pbkdf2Result, compareHash, compareSalt]);

  const copyToClipboard = async (text: string, item: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item);
      setTimeout(() => setCopiedItem(''), 2000);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setSettings(prev => ({ ...prev, inputFormat: 'file' }));
      setInputText('');
    }
  };

  const clearAll = () => {
    setInputText('');
    setPbkdf2Result(null);
    setCompareHash('');
    setCompareSalt('');
    setUploadedFile(null);
  };

  const exportResult = () => {
    if (!pbkdf2Result) return;

    const data = {
      input: pbkdf2Result.input,
      derivedKey: pbkdf2Result.derivedKey,
      salt: pbkdf2Result.salt,
      iterations: pbkdf2Result.iterations,
      outputLength: pbkdf2Result.outputLength,
      hashAlgorithm: pbkdf2Result.hashAlgorithm,
      securityLevel: pbkdf2Result.securityLevel,
      complianceScore: pbkdf2Result.complianceScore,
      strengthScore: pbkdf2Result.strengthScore,
      timestamp: pbkdf2Result.timestamp,
      processingTime: pbkdf2Result.processingTime,
      settings,
      analytics
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pbkdf2-result-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getSecurityColor = (level: SecurityLevel): string => {
    switch (level) {
      case 'weak': return 'text-red-600 bg-red-50 border-red-200';
      case 'moderate': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'strong': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'enterprise': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'government': return 'text-amber-600 bg-amber-50 border-amber-200';
    }
  };

  const getStrengthColor = (score: number): string => {
    if (score < 40) return 'text-red-600 bg-red-50 border-red-200';
    if (score < 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (score < 80) return 'text-blue-600 bg-blue-50 border-blue-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  // Auto-generate on input change
  useEffect(() => {
    if (settings.autoGenerate && inputText.trim()) {
      const timeoutId = setTimeout(generatePBKDF2, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [inputText, settings.autoGenerate, generatePBKDF2]);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-purple-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Enterprise Security Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/pbkdf2-hash-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/pbkdf2-hash-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/pbkdf2-hash-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="PBKDF2 Hash Generator Tool Interface" />
        <meta property="og:site_name" content="Enterprise Security Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/pbkdf2-hash-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/pbkdf2-hash-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Professional PBKDF2 Key Derivation Calculator" />
        <meta name="twitter:creator" content="@enterprisetools" />
        <meta name="twitter:site" content="@enterprisetools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#7c3aed"} />
        <meta name="msapplication-TileColor" content="#7c3aed" />
        <meta name="application-name" content="PBKDF2 Generator" />
        <meta name="apple-mobile-web-app-title" content="PBKDF2 Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Enterprise Tool Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "PBKDF2 Hash Generator",
            "description": "Enterprise-grade PBKDF2 password hash generator with configurable iterations and advanced security features",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/pbkdf2-hash-generator",
            "softwareVersion": "1.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Enterprise Security Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "3247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "PBKDF2 Key Derivation",
              "Enterprise Password Hashing",
              "Configurable Iterations",
              "Multiple Hash Algorithms", 
              "Secure Salt Generation",
              "Password Strength Analysis",
              "Enterprise Compliance",
              "Authentication System Integration"
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is PBKDF2 and why use it for password hashing?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "PBKDF2 (Password-Based Key Derivation Function 2) is specifically designed for secure password hashing and key derivation. It uses configurable iterations and salt to slow down brute-force attacks, making it ideal for authentication systems and secure password storage."
                }
              },
              {
                "@type": "Question", 
                "name": "How many iterations should I use for PBKDF2?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "For 2025, use at least 100,000 iterations for SHA-256. Enterprise applications often use 300,000+ iterations. The goal is to make password hashing slow enough to deter attackers while remaining acceptable for legitimate authentication."
                }
              },
              {
                "@type": "Question",
                "name": "What's the difference between PBKDF2 algorithms (SHA1, SHA256, SHA512)?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "SHA1 is deprecated for security reasons. SHA256 offers good balance of security and performance. SHA512 provides maximum security but requires more computational resources. Most enterprise applications use SHA256 for optimal security-performance balance."
                }
              },
              {
                "@type": "Question",
                "name": "How long should the salt be for PBKDF2?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Use at least 16 bytes (128 bits) of cryptographically random salt. Enterprise applications often use 32 bytes for additional security. Each password should have a unique salt to prevent rainbow table attacks."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Enterprise Schema */}
        {analytics.totalHashes > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SecurityAudit",
              "auditStatus": "Active",
              "securityLevel": `${analytics.securityScore}% Security Score`,
              "complianceScore": `${analytics.complianceScore}% Compliance`,
              "totalAssessments": analytics.totalHashes,
              "strongPasswords": analytics.strongPasswords,
              "averageIterations": analytics.averageIterations,
              "auditDate": new Date().toISOString()
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-6xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Key className="w-8 h-8 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    PBKDF2 Hash Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enterprise password-based key derivation function with configurable security
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Security Level Indicator */}
                {pbkdf2Result && (
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getSecurityColor(pbkdf2Result.securityLevel)}`}>
                    <Shield className="w-4 h-4" />
                    <span className="text-xs font-medium capitalize">
                      {pbkdf2Result.securityLevel}
                    </span>
                  </div>
                )}

                {/* Password Strength Indicator */}
                {pbkdf2Result && (
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getStrengthColor(pbkdf2Result.strengthScore)}`}>
                    <Zap className="w-4 h-4" />
                    <span className="text-xs font-medium">
                      {pbkdf2Result.strengthScore}% Strong
                    </span>
                  </div>
                )}

                {/* Enterprise Mode Indicator */}
                {settings.showEnterpriseMode && (
                  <div className="flex items-center space-x-2 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">
                    <Building className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                      Enterprise
                    </span>
                  </div>
                )}

                {/* Iterations Indicator */}
                {pbkdf2Result && (
                  <div className="flex items-center space-x-2 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-full">
                    <Timer className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                      {pbkdf2Result.iterations.toLocaleString()} iter
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                  aria-label="Toggle analytics panel"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                  aria-label="Toggle settings panel"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                  aria-label="Toggle dark/light theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  aria-label={isFullscreen ? "Exit fullscreen mode" : "Enter fullscreen mode"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              {/* Input Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Password / Input Data
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title={showPassword ? "Hide Password" : "Show Password"}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>
                    <label className="relative cursor-pointer bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-md transition-colors">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload File
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        className="sr-only"
                        aria-label="Upload file for PBKDF2 key derivation"
                      />
                    </label>
                    <button
                      onClick={clearAll}
                      className="p-2 rounded-md bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 transition-colors"
                      title="Clear All"
                      aria-label="Clear all input and results"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {uploadedFile ? (
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          {uploadedFile.name}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          {(uploadedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  showPassword ? (
                    <textarea
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder="Enter password or text for PBKDF2 key derivation..."
                      className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white resize-none font-mono"
                      aria-label="Input password for PBKDF2 key derivation"
                    />
                  ) : (
                    <input
                      type="password"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder="Enter password or text for PBKDF2 key derivation..."
                      className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                      aria-label="Input password for PBKDF2 key derivation"
                    />
                    ))}
                
               

                {/* Password Strength Indicator */}
                {inputText && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Password Strength
                      </span>
                      <span className="text-sm font-bold text-gray-800 dark:text-white">
                        {calculatePasswordStrength(inputText)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          calculatePasswordStrength(inputText) < 40 ? 'bg-red-500' :
                          calculatePasswordStrength(inputText) < 60 ? 'bg-yellow-500' :
                          calculatePasswordStrength(inputText) < 80 ? 'bg-blue-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${calculatePasswordStrength(inputText)}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Format: {settings.inputFormat}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Length: {uploadedFile ? uploadedFile.size : inputText.length} {uploadedFile ? 'bytes' : 'chars'}
                    </span>
                    <span className="text-sm text-blue-600 dark:text-blue-400">
                      {settings.iterations.toLocaleString()} iterations
                    </span>
                  </div>
                  <button
                    onClick={generatePBKDF2}
                    disabled={isGenerating || (!inputText.trim() && !uploadedFile)}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    aria-label="Generate PBKDF2 hash"
                  >
                    {isGenerating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4" />
                    )}
                    <span>{isGenerating ? 'Deriving...' : 'Derive Key'}</span>
                  </button>
                </div>
              </div>

              {/* Result Section */}
              {pbkdf2Result && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                      PBKDF2 Derived Key
                    </h2>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={exportResult}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Export Result"
                        aria-label="Export PBKDF2 result"
                      >
                        <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      </button>
                      <button
                        onClick={() => copyToClipboard(pbkdf2Result.derivedKey, 'key')}
                        className="p-2 rounded-md bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 transition-colors"
                        title="Copy Derived Key"
                        aria-label="Copy PBKDF2 derived key to clipboard"
                      >
                        {copiedItem === 'key' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Derived Key ({settings.outputFormat})
                      </label>
                      <div className="relative">
                        <textarea
                          value={pbkdf2Result.derivedKey}
                          readOnly
                          rows={3}
                          className="w-full p-3 font-mono text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none resize-none"
                          aria-label="Generated PBKDF2 derived key"
                        />
                        <div className="absolute right-2 top-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {pbkdf2Result.derivedKey.length} chars
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Salt (Hex)
                      </label>
                      <div className="relative">
                        <textarea
                          value={pbkdf2Result.salt}
                          readOnly
                          rows={2}
                          className="w-full p-3 font-mono text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none resize-none"
                          aria-label="Generated salt for PBKDF2"
                        />
                        <button
                          onClick={() => copyToClipboard(pbkdf2Result.salt, 'salt')}
                          className="absolute right-2 top-2 p-1 rounded bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                          title="Copy Salt"
                          aria-label="Copy salt to clipboard"
                        >
                          {copiedItem === 'salt' ? (
                            <Check className="w-3 h-3 text-gray-600 dark:text-gray-300" />
                          ) : (
                            <Copy className="w-3 h-3 text-gray-600 dark:text-gray-300" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Security Information */}
                    <div className={`p-4 rounded-lg border ${getSecurityColor(pbkdf2Result.securityLevel)}`}>
                      <div className="flex items-center space-x-2 mb-3">
                        <Shield className="w-5 h-5" />
                        <span className="text-sm font-medium capitalize">
                          {pbkdf2Result.securityLevel} Security Level
                        </span>
                        <div className="ml-auto flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Zap className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {pbkdf2Result.strengthScore}% Strength
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Award className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {pbkdf2Result.complianceScore}% Compliant
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <div>Algorithm: PBKDF2-{pbkdf2Result.hashAlgorithm}</div>
                          <div>Iterations: {pbkdf2Result.iterations.toLocaleString()}</div>
                          <div>Output Length: {pbkdf2Result.outputLength} bytes</div>
                        </div>
                        <div>
                          <div>Salt Length: {pbkdf2Result.salt.length / 2} bytes</div>
                          <div>Processing Time: {pbkdf2Result.processingTime.toFixed(2)}ms</div>
                          <div>Input Length: {pbkdf2Result.inputLength} characters</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Algorithm:</span>
                        <p className="font-medium text-gray-900 dark:text-white">PBKDF2</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">PRF:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{pbkdf2Result.hashAlgorithm}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Format:</span>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">{pbkdf2Result.outputFormat}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Generated:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{pbkdf2Result.timestamp.toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hash Comparison */}
              {settings.enableComparison && pbkdf2Result && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Key Verification
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Compare with Expected Derived Key
                      </label>
                      <textarea
                        value={compareHash}
                        onChange={(e) => setCompareHash(e.target.value)}
                        placeholder="Enter expected PBKDF2 derived key to compare..."
                        rows={3}
                        className="w-full p-3 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                        aria-label="Enter derived key for comparison"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Compare Salt (Optional)
                      </label>
                      <input
                        type="text"
                        value={compareSalt}
                        onChange={(e) => setCompareSalt(e.target.value)}
                        placeholder="Enter expected salt (hex)..."
                        className="w-full p-3 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                        aria-label="Enter salt for comparison"
                      />
                    </div>

                    {compareHash.trim() && (
                      <div className={`p-3 rounded-lg border ${
                        compareResults() 
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}>
                        <div className="flex items-center space-x-2">
                          {compareResults() ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                              <span className="text-green-800 dark:text-green-300 font-medium">
                                Keys match! PBKDF2 verification successful.
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                              <span className="text-red-800 dark:text-red-300 font-medium">
                                Keys do not match! Authentication failed.
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    PBKDF2 Configuration
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Hash Algorithm */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        PRF Algorithm
                      </label>
                      <select
                        value={settings.hashAlgorithm}
                        onChange={(e) => setSettings(prev => ({ ...prev, hashAlgorithm: e.target.value as HashAlgorithm }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                        aria-label="Select hash algorithm"
                      >
                        <option value="SHA1">SHA1 (Legacy)</option>
                        <option value="SHA256">SHA256 (Recommended)</option>
                        <option value="SHA512">SHA512 (Maximum Security)</option>
                      </select>
                    </div>

                    {/* Input Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Input Format
                      </label>
                      <select
                        value={settings.inputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, inputFormat: e.target.value as InputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                        aria-label="Select input format"
                      >
                        <option value="text">Plain Text</option>
                        <option value="file">File Upload</option>
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                        <option value="utf8">UTF-8</option>
                      </select>
                    </div>

                    {/* Output Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Output Format
                      </label>
                      <select
                        value={settings.outputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, outputFormat: e.target.value as OutputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                        aria-label="Select output format"
                      >
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                        <option value="decimal">Decimal</option>
                      </select>
                    </div>

                    {/* Output Length */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Output Length: {settings.outputLength} bytes
                      </label>
                      <input
                        type="range"
                        min="8"
                        max="128"
                        value={settings.outputLength}
                        onChange={(e) => setSettings(prev => ({ ...prev, outputLength: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        aria-label="Adjust output length"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>8 bytes</span>
                        <span>128 bytes</span>
                      </div>
                    </div>

                    {/* Iterations */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Iterations: {settings.iterations.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="1000"
                        max="1000000"
                        step="1000"
                        value={settings.iterations}
                        onChange={(e) => setSettings(prev => ({ ...prev, iterations: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        aria-label="Adjust iteration count"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>1K</span>
                        <span>1M</span>
                      </div>
                    </div>

                    {/* Salt Configuration */}
                    <div>
                      <label className="flex items-center space-x-3 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={settings.autoGenerateSalt}
                          onChange={(e) => setSettings(prev => ({ ...prev, autoGenerateSalt: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          aria-label="Auto-generate salt"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Auto-Generate Salt</span>
                      </label>
                      
                      {!settings.autoGenerateSalt && (
                        <input
                          type="text"
                          value={settings.customSalt}
                          onChange={(e) => setSettings(prev => ({ ...prev, customSalt: e.target.value }))}
                          placeholder="Enter custom salt (hex)..."
                          className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white font-mono"
                          aria-label="Enter custom salt"
                        />
                      )}

                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Salt Length: {settings.saltLength} bytes
                        </label>
                        <input
                          type="range"
                          min="8"
                          max="64"
                          value={settings.saltLength}
                          onChange={(e) => setSettings(prev => ({ ...prev, saltLength: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          aria-label="Adjust salt length"
                        />
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>8</span>
                          <span>64</span>
                        </div>
                      </div>
                    </div>

                    {/* Enterprise Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Options</h4>
                      {[
                        { key: 'autoGenerate', label: 'Auto Generate', desc: 'Generate key as you type' },
                        { key: 'enableComparison', label: 'Key Verification', desc: 'Enable key comparison tools' },
                        { key: 'showAnalysis', label: 'Security Analysis', desc: 'Show detailed security metrics' },
                        { key: 'showEnterpriseMode', label: 'Enterprise Mode', desc: 'Enable enterprise features' },
                        { key: 'caseSensitive', label: 'Case Sensitive', desc: 'Consider letter case in input' },
                        { key: 'includeTimestamp', label: 'Include Timestamp', desc: 'Add timestamp for uniqueness' },
                        { key: 'showSecurityInfo', label: 'Security Information', desc: 'Show compliance information' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof PBKDF2Settings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 mt-0.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                              aria-label={`Toggle ${label}`}
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>

                    {/* Security Recommendations */}
                    {settings.showSecurityInfo && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                              Security Recommendations
                            </p>
                            <ul className="text-xs text-blue-700 dark:text-blue-400 mt-1 space-y-1">
                              <li>• Use at least 100,000 iterations for SHA-256</li>
                              <li>• Use minimum 16-byte cryptographically random salt</li>
                              <li>• Choose SHA-512 for maximum security</li>
                              <li>• Store salt separately from derived key</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    PBKDF2 Analytics
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalHashes}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Keys Derived
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.averageStrengthScore}%
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Strength
                        </div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.averageIterations.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Iterations
                        </div>
                      </div>
                      <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                          {analytics.complianceScore}%
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Compliance
                        </div>
                      </div>
                    </div>

                    {/* Algorithm Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Algorithm Usage
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.algorithmDistribution).map(([algo, count]) => (
                          count > 0 && (
                            <div key={algo} className="flex justify-between items-center text-sm">
                              <span className="text-gray-600 dark:text-gray-400">{algo}</span>
                              <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>

                    {/* Security Level Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Security Levels
                      </h4>
                      <div className="space-y-2">
                        {analytics.hashHistory.length > 0 && (
                          (() => {
                            const levelCounts = analytics.hashHistory.reduce((acc, entry) => {
                              acc[entry.securityLevel] = (acc[entry.securityLevel] || 0) + 1;
                              return acc;
                            }, {} as Record<SecurityLevel, number>);

                            return Object.entries(levelCounts).map(([level, count]) => (
                              <div key={level} className="flex justify-between items-center text-sm">
                                <span className="text-gray-600 dark:text-gray-400 capitalize">{level}</span>
                                <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                              </div>
                            ));
                          })()
                        )}
                      </div>
                    </div>

                    {/* Performance Stats */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Performance
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Strong Passwords</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.strongPasswords}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Avg Time</span>
                          <span className="font-medium text-gray-800 dark:text-white">
                            {analytics.totalHashes > 0 ? Math.round(analytics.totalProcessingTime / analytics.totalHashes) : 0}ms
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Avg Input</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.averageInputLength} chars</span>
                        </div>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Recent Activity
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {analytics.hashHistory.slice(0, 5).map((entry, index) => (
                          <div key={index} className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <div className="flex justify-between">
                              <span>{entry.inputLength} chars</span>
                              <span>{entry.strengthScore}% strong</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="capitalize">{entry.securityLevel}</span>
                              <span>{entry.iterations.toLocaleString()} iter</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {entry.timestamp.toLocaleTimeString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* PBKDF2 Information */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          PBKDF2 Specifications
                        </span>
                      </div>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>• Password-Based Key Derivation Function 2</li>
                        <li>• RFC 2898 / PKCS #5 v2.0 standard</li>
                        <li>• Configurable iterations for security tuning</li>
                        <li>• Cryptographically secure salt requirement</li>
                        <li>• Designed to resist brute-force attacks</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Hidden SEO Content */}
          <div className="sr-only" aria-hidden="true">
            <h2>Enterprise PBKDF2 Password Hash Generator and Key Derivation Tool</h2>
            <p>
              Generate enterprise-grade PBKDF2 password hashes and derive cryptographic keys with 
              configurable iterations, salt, and multiple hash algorithms. Our advanced PBKDF2 
              calculator provides secure password storage, authentication system integration, and 
              enterprise compliance for web applications, backend systems, and security frameworks.
            </p>
            
            <h3>Advanced PBKDF2 Enterprise Features</h3>
            <ul>
              <li>Configurable PBKDF2 key derivation with up to 1 million iterations</li>
              <li>Multiple PRF algorithms: SHA1, SHA256, SHA512</li>
              <li>Cryptographically secure salt generation and custom salt support</li>
              <li>Variable output length from 8 to 128 bytes</li>
              <li>Password strength analysis and security scoring</li>
              <li>Enterprise compliance verification and audit trails</li>
              <li>Multiple input/output formats for system integration</li>
              <li>Real-time security level assessment and recommendations</li>
            </ul>
            
            <h3>PBKDF2 Security and Authentication Applications</h3>
            <ul>
              <li>Secure password storage for web applications and databases</li>
              <li>User authentication systems and login verification</li>
              <li>Enterprise directory services and identity management</li>
              <li>API key derivation and secure token generation</li>
              <li>Cryptographic key derivation for encryption systems</li>
              <li>Mobile app security and secure credential storage</li>
              <li>Blockchain and cryptocurrency wallet security</li>
              <li>IoT device authentication and secure communications</li>
            </ul>
            
            <h3>Enterprise Password Security Standards</h3>
            <p>
              PBKDF2 is widely adopted in enterprise environments and recommended by security 
              frameworks including OWASP, NIST, and industry standards. With configurable 
              iterations and robust salt handling, it provides defense against rainbow table 
              attacks, brute-force attacks, and dictionary attacks while maintaining reasonable 
              performance for legitimate authentication operations.
            </p>
            
            <h3>Performance and Security Tuning</h3>
            <p>
              Modern PBKDF2 implementations require careful tuning of iteration counts to balance 
              security and performance. For 2025, security experts recommend at least 100,000 
              iterations for SHA-256, with enterprise applications often using 300,000+ iterations. 
              Our tool provides real-time performance metrics to help optimize security settings.
            </p>
            
            <h3>Integration with Development Frameworks</h3>
            <p>
              PBKDF2 is natively supported in most programming languages and frameworks including 
              Node.js, Python, Java, .NET, PHP, and Ruby. Our tool generates implementation-ready 
              parameters and provides export functionality for seamless integration with existing 
              authentication systems and security frameworks.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PBKDF2HashGenerator;